import {
  HeaderLegendList,
  useGlassHeaderInset,
} from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { LevelDetailsSurfaceHeader } from "#level-details-surface-header";
import { useLevelDetailsExpanded } from "#use-level-details-expanded";
import { useLevelSurfaceSlidePages } from "#use-level-surface-slide-pages";
import { CardDetailsTileListSkeleton } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.link.details.list";
import { MorphemeDetailsTileList } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.details.list";
import {
  LEVEL_SURFACE_GRID_GAP,
  LevelSurfacesLoadingPage,
  LevelSurfaceGlyphGrid,
  useLevelTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import {
  Surface,
  SurfaceHeaderRowContext,
  createSurfaceHeaderRow,
  getSurfaceGridLayout,
  SURFACE_GRID_GAP,
} from "@-/dev.improve.haniverse.components.ui.surface";
import type { BottomSheetHandle } from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useCardFilterStaticFactsQueryOptions } from "@-/dev.improve.haniverse.lib.cards.filter.facts";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  BrowseLevelsLayoutContext,
  type LevelSurfacePart,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import {
  type BatchedGraphemeTileData,
  type GetGraphemeCardStage,
  type GlyphStyle,
  getMorphemeGlyphTileGraphemeGlyphStyle,
  MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT,
  type MorphemeGlyphTileStage,
  useBatchedGraphemeCardStages,
  useBatchedGraphemeTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { formatPronunciationLevelLabel } from "@-/dev.improve.haniverse.lib.level";
import {
  type IndexedLevel,
  type IndexedPronunciationLevel,
  type IndexedWordLevel,
  type LevelsListEntry,
  getLevelsListEntryLabel,
  isIndexedCharacterLevel,
  isIndexedPronunciationLevel,
  isIndexedWordLevel,
} from "#levels-list-entry";
import { filterNullable, uniq } from "@-/com.npmjs.array-utils-ts";
import split from "@-/com.npmjs.just-split";
import { queryOptions, useQueries, useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  type LegendListRef,
  type LegendListRenderItemProps,
  type OnViewableItemsChangedInfo,
  type ViewabilityConfig,
} from "@-/com.npmjs.legendapp__list";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import useLatest from "@-/com.npmjs.use-latest";
import { Animated, Platform, ScrollView, View } from "@-/com.npmjs.react-native";
import { useIsServerRender } from "#use-is-server-render";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { useNavigationTreeFocus } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { isTransientNetworkError } from "@-/dev.improve.haniverse.lib.error-classification";
import { LockedLevelSurface } from "#locked-level-surface";
import { getLevelDetailsSheetGate } from "#get-level-details-sheet-gate";
import { LevelDetailsSheet } from "#level-details-sheet";
import { LockedWordLevelSurface, WordLevelSurface } from "#word-level-surface";
import { useWordLevelsListState, type WordLevelsListState } from "#use-word-levels-list-state";
import { WordLevelGlyphGrid } from "#word-level-glyph-grid";
import { LEVELS_LIST_TOP_GAP } from "#levels-list-top-gap";

export type LevelsListViewMode = "glyph-tiles" | "detail-tiles";

/**
 * One rendered row. The upgrade prompt is a real row in the data — inserted immediately before the
 * first locked level — rather than a viewport overlay that pops in past a scroll threshold. It
 * scrolls with the content like any other row, and `stickyHeaderIndices` pins it once it reaches
 * the top so it stays on screen while the user keeps scrolling through the blurred levels.
 */
type LevelsListRow =
  | { kind: "level"; level: LevelsListEntry }
  | { kind: "upgrade-prompt"; level?: undefined };

/**
 * Splices the upgrade prompt in ahead of the first locked level. Returns the row list plus that
 * row's index for `stickyHeaderIndices` (`null` when there is nothing locked to gate).
 */
function buildLevelsListRows(
  levelsList: readonly LevelsListEntry[],
  hasPrompt: boolean,
): { promptIndex: number | null; rows: LevelsListRow[] } {
  const firstLockedIndex = levelsList.findIndex((item) => item.isLocked);
  const rows: LevelsListRow[] = levelsList.map((level) => ({ kind: "level", level }));
  if (!hasPrompt || firstLockedIndex === -1) {
    return { promptIndex: null, rows };
  }
  rows.splice(firstLockedIndex, 0, { kind: "upgrade-prompt" });
  return { promptIndex: firstLockedIndex, rows };
}

function levelsListRowKeyExtractor(row: LevelsListRow) {
  if (row.kind === "upgrade-prompt") {
    return "upgrade-prompt";
  }
  if (isIndexedWordLevel(row.level)) {
    return `word-level-${row.level.wordLevel.index}`;
  }
  if (isIndexedPronunciationLevel(row.level)) {
    return `pronunciation-level-${row.level.pronunciationLevel.index}`;
  }
  return (row.level.level.slug ?? "level") + row.level.index;
}

/** Recycle character surfaces only into character surfaces, and word surfaces into word ones. */
function levelsListRowItemType(row: LevelsListRow) {
  if (row.kind === "upgrade-prompt") {
    return "upgrade-prompt";
  }
  if (isIndexedWordLevel(row.level)) {
    return "word-level";
  }
  return isIndexedPronunciationLevel(row.level) ? "pronunciation-level" : "level";
}

/**
 * The character levels the details sheet pages through: pronunciation and word levels have no
 * details sheet.
 */
function useLevelDetailsLevels(levelsList: readonly LevelsListEntry[]) {
  return useMemo(
    () =>
      levelsList.filter(
        (entry): entry is IndexedLevel => isIndexedCharacterLevel(entry) && entry.isLocked !== true,
      ),
    [levelsList],
  );
}

// A level counts as "in view" once at least this fraction of its surface is on screen.
const CURRENT_LEVEL_VISIBLE_PERCENT_THRESHOLD = 1;

/** Index (plus optional offset/position) the list scrolls to on its first paint. */
export type LevelsListInitialScrollIndex = {
  index: number;
  viewOffset?: number;
  viewPosition?: number;
};

export interface LevelsListProps {
  /**
   * Extra bottom padding (px) added to the scroll content so the last level surface clears the
   * floating sub-tab bar instead of coming to rest permanently underneath it.
   */
  contentBottomPadding?: number;
  /** Extra top padding (px) added to the scroll content so it clears the floating toolbar overlay. */
  contentTopPadding?: number;
  /** Index (within `levelsList`) of the user's current level, or `null` if it isn't shown. */
  currentLevelIndex?: number | null;
  /**
   * Index the list is scrolled to on its very first paint (via LegendList's `initialScrollIndex`),
   * so the current level is shown immediately with no flash of Level 1 or visible scroll. Omit to
   * open at the top.
   */
  initialScrollIndex?: LevelsListInitialScrollIndex;
  /** Character levels, each optionally followed by the word levels placed after it. */
  levelsList: readonly LevelsListEntry[];
  ListFooterComponent?: ReactElement | null;
  /** Scrolls above the first level, like any row (e.g. the pack's description). */
  ListHeaderComponent?: ReactElement | null;
  listRef?: RefObject<LegendListRef | null>;
  lockedLevelPrompt?: ReactElement;
  /** Called whenever the current level's visibility in the viewport changes. */
  onCurrentLevelViewabilityChange?: (isInViewport: boolean) => void;
  /** User drag, wheel, or keyboard input; programmatic scrolling does not call this. */
  onManualScroll?: () => void;
  /**
   * Names what the list shows (e.g. its grade). Inside a `RouteBodyStateProvider`, a remount with
   * the same scope reopens at the row the learner left at the top, overriding `initialScrollIndex`;
   * a different scope discards that position.
   */
  scrollPositionScope?: string;
  viewMode?: LevelsListViewMode;
}

/**
 * Builds a stable `onViewableItemsChanged` + `viewabilityConfig` pair that reports whether the
 * current level is on screen. The callback identity must stay stable for the list's lifetime
 * (LegendList/FlatList latch the first one), so we read the live index/callback through refs.
 */
function useCurrentLevelViewability({
  currentLevelIndex,
  onCurrentLevelViewabilityChange,
  promptIndex,
}: {
  currentLevelIndex: number | null;
  onCurrentLevelViewabilityChange?: (isInViewport: boolean) => void;
  promptIndex: number | null;
}) {
  const currentLevelIndexRef = useLatest(currentLevelIndex);
  const onChangeRef = useLatest(onCurrentLevelViewabilityChange);
  const promptIndexRef = useLatest(promptIndex);

  const viewabilityConfig = useRef<ViewabilityConfig>({
    itemVisiblePercentThreshold: CURRENT_LEVEL_VISIBLE_PERCENT_THRESHOLD,
  }).current;

  const onViewableItemsChanged = useRef(
    ({ viewableItems }: OnViewableItemsChangedInfo<LevelsListRow>) => {
      const targetIndex = currentLevelIndexRef.current;
      // Tokens carry ROW indices; the prompt row shifts every level after it by one.
      const isInViewport =
        targetIndex !== null &&
        viewableItems.some(
          (token) =>
            token.item.kind === "level" &&
            toLevelIndex(token.index, promptIndexRef.current) === targetIndex &&
            token.isViewable,
        );
      onChangeRef.current?.(isInViewport);
    },
  ).current;

  return { onViewableItemsChanged, viewabilityConfig };
}

// Rough first-render height hint (px) for a glyph-tiles level surface (header + ~2 tile rows).
// LegendList refines from real measurements; this just avoids the one-at-a-time layout pass.
const ESTIMATED_LEVEL_SURFACE_SIZE = 180;
// Pixels of off-screen content to pre-render ahead of the viewport (LegendList default is 250). Kept
// small so the first Browse paint mounts only a few surfaces past the fold instead of a long run.
const LEVELS_DRAW_DISTANCE = 100;

const LIST_HORIZONTAL_PADDING = 4;
const CARD_HORIZONTAL_PADDING = 4;
// `Surface` outline (`border`) is 1px on each side, inside the card padding.
const CARD_BORDER_WIDTH = 1;

/**
 * Pre-compute the glyph fontSize from window width + columns so every tile in the levels list
 * renders at the right size on first commit. Without this, each tile relies on its own
 * `onLayout` measurement pass, and we've seen tiles get stuck at `opacity-0` when that layout
 * event never lands (list recycling + `absolute inset-0` measurement race).
 */
function computeTileGlyphStyle({
  columns,
  rowWidth,
}: {
  columns: number;
  rowWidth: number;
}): GlyphStyle | null {
  if (rowWidth <= 0 || columns <= 0) {
    return null;
  }
  const usable = rowWidth - CARD_HORIZONTAL_PADDING * 2 - CARD_BORDER_WIDTH * 2;
  const totalGap = LEVEL_SURFACE_GRID_GAP * Math.max(0, columns - 1);
  const tileSize = Math.max(1, Math.floor((usable - totalGap) / columns));
  const glyphZoneHeight = Math.max(1, tileSize - MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT * 2);
  return getMorphemeGlyphTileGraphemeGlyphStyle({
    glyphZoneHeight,
    glyphZoneWidth: tileSize,
  });
}

export function LevelsList(props: LevelsListProps) {
  return (
    <MorphemeTilePresentationProvider>
      <LevelsListContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}

function LevelsListContent({
  contentBottomPadding = 0,
  contentTopPadding = 0,
  currentLevelIndex = null,
  initialScrollIndex,
  levelsList,
  ListFooterComponent,
  ListHeaderComponent,
  listRef,
  lockedLevelPrompt,
  onCurrentLevelViewabilityChange,
  onManualScroll,
  scrollPositionScope = "",
  viewMode = "glyph-tiles",
}: LevelsListProps) {
  const promptIndex = useMemo(() => {
    if (lockedLevelPrompt === undefined) {
      return null;
    }
    const firstLockedIndex = levelsList.findIndex((item) => item.isLocked);
    return firstLockedIndex === -1 ? null : firstLockedIndex;
  }, [levelsList, lockedLevelPrompt]);
  const { onViewableItemsChanged, viewabilityConfig } = useCurrentLevelViewability({
    currentLevelIndex,
    onCurrentLevelViewabilityChange,
    promptIndex,
  });
  const isServerRender = useIsServerRender();
  const internalListRef = useRef<LegendListRef>(null);
  const effectiveListRef = listRef ?? internalListRef;
  const glassHeaderInset = useGlassHeaderInset();

  // The list needs on-device data and viewport measurements. Keep skeletons identical on the
  // server and hydrating pass, then mount the list after hydration.
  if (isServerRender) {
    return <LevelSurfacesLoadingPage />;
  }

  return (
    // Beneath glass chrome the list reaches up under the status bar, past this frame.
    <View
      className={`relative min-h-0 flex-1 ${glassHeaderInset === undefined ? "overflow-hidden" : ""}`}
    >
      <LevelButtonsLevelsList
        contentBottomPadding={contentBottomPadding}
        contentTopPadding={contentTopPadding}
        initialScrollIndex={initialScrollIndex}
        levelsList={levelsList}
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        listRef={effectiveListRef}
        lockedLevelPrompt={lockedLevelPrompt}
        onManualScroll={onManualScroll}
        onViewableItemsChanged={onViewableItemsChanged}
        // The two view modes size their rows differently, so neither restores the other's offset.
        scrollPositionScope={`${viewMode}/${scrollPositionScope}`}
        viewabilityConfig={viewabilityConfig}
        viewMode={viewMode}
      />
    </View>
  );
}

type InnerLevelsListProps = {
  contentBottomPadding: number;
  contentTopPadding: number;
  initialScrollIndex?: LevelsListInitialScrollIndex;
  levelsList: readonly LevelsListEntry[];
  ListFooterComponent?: ReactElement | null;
  ListHeaderComponent?: ReactElement | null;
  listRef?: RefObject<LegendListRef | null>;
  lockedLevelPrompt?: ReactElement;
  onManualScroll?: () => void;
  onViewableItemsChanged: (info: OnViewableItemsChangedInfo<LevelsListRow>) => void;
  scrollPositionScope: string;
  viewabilityConfig: ViewabilityConfig;
  viewMode: LevelsListViewMode;
};

const LEVELS_LIST_CONTENT_CONTAINER_CLASS_NAME = "px-1 pb-1";
/**
 * Sticky headers require an Animated ScrollView (LegendList reads the scroll position off an
 * `Animated.Value` to drive the pinned row). Created once at module scope — building it inside
 * `renderScrollComponent` would make a new component type on every render and remount the scroller.
 */
// eslint-disable-next-line no-top/no-top-level-side-effects -- component type must be created once at module load, not per render
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

/**
 * Wrapper for the prompt row. Deliberately transparent: once pinned, the blurred level surfaces
 * scroll underneath, and the prompt's glass material is meant to show them passing beneath it (the
 * prompt supplies its own opaque background where liquid glass is unavailable).
 */
function LevelsUpgradePromptRow({ children }: { children: ReactNode }) {
  return <View className="px-2 pt-1 pb-2">{children}</View>;
}

/**
 * `viewabilityConfig`/`onViewableItemsChanged` report indices into the ROW list, which includes the
 * spliced-in prompt. The caller tracks the current level by its index in `levelsList`, so shift the
 * reported indices back into level space before handing them up.
 */
function toLevelIndex(rowIndex: number, promptIndex: number | null) {
  if (promptIndex === null || rowIndex < promptIndex) {
    return rowIndex;
  }
  return rowIndex - 1;
}

/** Inverse of `toLevelIndex`: a level's index in `levelsList` → its index in the row list. */
function toRowIndex(levelIndex: number, promptIndex: number | null) {
  if (promptIndex === null || levelIndex < promptIndex) {
    return levelIndex;
  }
  return levelIndex + 1;
}

/**
 * The caller names a LEVEL index; the list is indexed by rows, which include the prompt.
 * `HeaderLegendList` prefers the position saved by `scrollPosition` over this index, and owns the
 * web path (LegendList's `initialScrollIndex` paints a blank list on web).
 */
function useRowScrollIndex(
  initialScrollIndex: LevelsListInitialScrollIndex | undefined,
  promptIndex: number | null,
) {
  return useMemo(
    () =>
      initialScrollIndex === undefined
        ? undefined
        : { ...initialScrollIndex, index: toRowIndex(initialScrollIndex.index, promptIndex) },
    [initialScrollIndex, promptIndex],
  );
}

function LevelButtonsLevelsList({
  contentBottomPadding,
  contentTopPadding,
  initialScrollIndex,
  levelsList,
  ListFooterComponent,
  ListHeaderComponent,
  listRef,
  lockedLevelPrompt,
  onManualScroll,
  onViewableItemsChanged,
  scrollPositionScope,
  viewabilityConfig,
  viewMode,
}: InnerLevelsListProps) {
  const push = useMorphemePanelsHrefNavigator().push.mutate;
  const onNavigate = useCallback(
    (morpheme: string) => {
      push({
        morpheme,
        prefix: { href: "/(nav)/(app)/browse/morpheme/[morpheme]", params: { view: "mnemonics" } },
      });
    },
    [push],
  );
  const { promptIndex, rows } = useMemo(
    () => buildLevelsListRows(levelsList, lockedLevelPrompt !== undefined),
    [levelsList, lockedLevelPrompt],
  );
  const { focusVersion: navigationTreeFocusVersion, isFocusedRef: isNavigationTreeFocusedRef } =
    useNavigationTreeFocus();
  const rowScrollIndex = useRowScrollIndex(initialScrollIndex, promptIndex);
  // With opaque chrome the list viewport already starts below the toolbar and pack tabs.
  // Beneath glass chrome (iOS 26) it stops below the bar and the grade tabs instead.
  const glassHeaderInset = useGlassHeaderInset();
  const levelDetailsSheetRef = useRef<BottomSheetHandle>(null);
  const presentedSheetOpenRequestVersionRef = useRef(0);
  const [activeLevelPosition, setActiveLevelPosition] = useState<number | null>(null);
  const [sheetOpenRequestVersion, setSheetOpenRequestVersion] = useState(0);
  const [windowDimensions] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  const [layoutWidth, setLayoutWidth] = useState(0);
  // Before onLayout, use the narrow dimension rather than inflating glyphs from the window width.
  const width = layoutWidth || Math.min(windowDimensions.width, windowDimensions.height);
  const {
    glyphColumns: columns,
    surfaceColumns,
    surfaceWidth: rowWidth,
    tileSize,
  } = getSurfaceGridLayout(width - LIST_HORIZONTAL_PADDING * 2);
  const tileGlyphStyle = useMemo(
    () => computeTileGlyphStyle({ columns, rowWidth }),
    [columns, rowWidth],
  );
  const headerRows = useMemo(() => {
    const result = new Map<string, ReturnType<typeof createSurfaceHeaderRow>>();
    if (surfaceColumns === 1) {
      return result;
    }
    let row = createSurfaceHeaderRow(rowWidth);
    let column = 0;
    for (const item of rows) {
      // A full-width upgrade prompt ends a partial row and starts a new one below it.
      if (item.kind === "upgrade-prompt") {
        row = createSurfaceHeaderRow(rowWidth);
        column = 0;
        continue;
      }
      result.set(levelsListRowKeyExtractor(item), row);
      column += 1;
      if (column === surfaceColumns) {
        row = createSurfaceHeaderRow(rowWidth);
        column = 0;
      }
    }
    return result;
    // Width changes invalidate natural measurements even when the column count stays the same.
  }, [rows, surfaceColumns, rowWidth]);

  // Batch the SRS-stage + pinyin-slice reads for EVERY grapheme in the whole list into ONE query
  // each, here, instead of per-level (let alone per-tile). Per-level queries resolved independently
  // and asynchronously — each level's resolution re-rendered that level surface, changed its height,
  // and forced the LegendList to re-lay-out, producing ~100+ layout commits over ~2s that blocked the
  // JS thread and left Browse blank. Two whole-list queries resolve once, so the list lays out once.
  const levelDetailsLevels = useLevelDetailsLevels(levelsList);
  const allGraphemes = useMemo(
    () =>
      uniq([
        ...levelDetailsLevels.flatMap((indexed) => indexed.level.characters),
        // A pronunciation level repeats earlier characters, which a filter may have hidden.
        ...levelsList.flatMap((entry) =>
          isIndexedPronunciationLevel(entry) && entry.isLocked !== true
            ? (entry.pronunciationLevel.pronunciationCharacters ?? [])
            : [],
        ),
      ]),
    [levelDetailsLevels, levelsList],
  );
  // Read the imperative ref (not a rendered boolean): React Native Screens can freeze Browse before
  // its blur render commits, leaving a rendered value stale while this stays current.
  const isStageQueryEnabled = () => isNavigationTreeFocusedRef.current;
  const batchedTileData = useBatchedGraphemeTileData(allGraphemes, {
    enabled: isStageQueryEnabled,
    // Restore the stage updates skipped while Browse was blurred once it is focused again.
    refetchVersion: navigationTreeFocusVersion + 1,
  });
  // A level's tile shows the card that level teaches (see `useLevelTileData`), read in one batch
  // over the same overlay rows.
  const getCardStage = useBatchedGraphemeCardStages(allGraphemes, {
    enabled: isStageQueryEnabled,
    refetchVersion: navigationTreeFocusVersion + 1,
  });
  const wordLevelsListState = useWordLevelsListState({ columns, tileSize });

  // Batch the whole list's completed-authoring reads (grapheme-file `version >= 0`, surfaced as a
  // non-null `completed_at` in the content DB) into ONE query for the entire list, alongside the
  // stage/slice batches above — never per tile. Feeds each tile's `fadeMissingMnemonicEnabled` fade.
  // Deterministic failures are thrown rather than left `undefined`: an errored read used to render
  // as "no tile is faded", indistinguishable from "every mnemonic is written" — see
  // `useCardFilterFactsByGrapheme`. A transient connection failure keeps the tiles visible without
  // the optional fade while the account drawer surfaces the live QueryCache error.
  const { data: staticFactsByGrapheme } = useQuery({
    ...useCardFilterStaticFactsQueryOptions(allGraphemes),
    enabled: isStageQueryEnabled,
    throwOnError: (error) => !isTransientNetworkError(error),
  });
  const completedByGrapheme = useMemo(() => {
    if (staticFactsByGrapheme === undefined) {
      return undefined;
    }
    const map = new Map<string, boolean>();
    for (const [grapheme, facts] of staticFactsByGrapheme) {
      map.set(grapheme, facts.completed);
    }
    return map;
  }, [staticFactsByGrapheme]);

  const levelDetailsSheetGate = getLevelDetailsSheetGate(levelDetailsLevels, activeLevelPosition);

  const openLevelDetails = useCallback((position: number) => {
    setActiveLevelPosition(position);
    setSheetOpenRequestVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    if (
      activeLevelPosition === null ||
      sheetOpenRequestVersion === presentedSheetOpenRequestVersionRef.current
    ) {
      return;
    }
    presentedSheetOpenRequestVersionRef.current = sheetOpenRequestVersion;
    levelDetailsSheetRef.current?.open();
  }, [activeLevelPosition, sheetOpenRequestVersion]);

  const renderSurface = useCallback(
    ({ item }: LegendListRenderItemProps<LevelsListRow>) => {
      if (item.kind === "upgrade-prompt") {
        return <LevelsUpgradePromptRow>{lockedLevelPrompt}</LevelsUpgradePromptRow>;
      }
      const indexedLevel = item.level;
      if (isIndexedWordLevel(indexedLevel)) {
        return (
          <WordLevelEntrySurface indexedWordLevel={indexedLevel} state={wordLevelsListState} />
        );
      }
      if (isIndexedPronunciationLevel(indexedLevel)) {
        return (
          <PronunciationLevelButtonsSurface
            batchedTileData={batchedTileData}
            columns={columns}
            completedByGrapheme={completedByGrapheme}
            defaultExpanded={viewMode === "detail-tiles"}
            getCardStage={getCardStage}
            glyphStyle={tileGlyphStyle ?? undefined}
            indexedPronunciationLevel={indexedLevel}
            onNavigate={onNavigate}
            wordState={wordLevelsListState}
          />
        );
      }
      return indexedLevel.isLocked ? (
        <LockedLevelSurface
          columns={columns}
          label={getLevelsListEntryLabel(indexedLevel)}
          level={indexedLevel.level}
        />
      ) : (
        <LevelButtonsSurface
          batchedTileData={batchedTileData}
          columns={columns}
          completedByGrapheme={completedByGrapheme}
          defaultExpanded={viewMode === "detail-tiles"}
          getCardStage={getCardStage}
          glyphStyle={tileGlyphStyle ?? undefined}
          indexedLevel={indexedLevel}
          onHeaderPress={() => {
            // The sheet pages through the unlocked character levels only.
            openLevelDetails(levelDetailsLevels.indexOf(indexedLevel));
          }}
          onNavigate={onNavigate}
        />
      );
    },
    [
      batchedTileData,
      columns,
      completedByGrapheme,
      getCardStage,
      levelDetailsLevels,
      lockedLevelPrompt,
      openLevelDetails,
      onNavigate,
      tileGlyphStyle,
      wordLevelsListState,
      viewMode,
    ],
  );

  const renderItem = useCallback(
    (props: LegendListRenderItemProps<LevelsListRow>) => {
      const itemKey = levelsListRowKeyExtractor(props.item);
      const row = headerRows.get(itemKey);
      const surface = renderSurface(props);
      return row === undefined ? (
        surface
      ) : (
        <SurfaceHeaderRowContext value={{ itemKey, row }}>{surface}</SurfaceHeaderRowContext>
      );
    },
    [headerRows, renderSurface],
  );

  return (
    <>
      <HeaderLegendList
        {...(Platform.OS === "web"
          ? {
              onKeyDown: onManualScroll,
              onPointerDown: onManualScroll,
              onWheel: onManualScroll,
            }
          : { onScrollBeginDrag: onManualScroll })}
        className="flex-1"
        columnWrapperStyle={{ columnGap: SURFACE_GRID_GAP }}
        contentContainerClassName={LEVELS_LIST_CONTENT_CONTAINER_CLASS_NAME}
        contentContainerStyle={{
          paddingBottom: contentBottomPadding,
          paddingTop: contentTopPadding + LEVELS_LIST_TOP_GAP,
        }}
        data={rows}
        // Keep the pre-rendered window small so the first Browse paint mounts only the level surfaces
        // near the viewport, not far-ahead ones — each extra mounted surface is more work on the one
        // paint the user is waiting for. The list still renders more as the user scrolls.
        drawDistance={LEVELS_DRAW_DISTANCE}
        // A first-render size hint per level surface, so LegendList can lay out in one pass and refine
        // from real measurements as they arrive (rather than measuring each surface one at a time).
        estimatedItemSize={ESTIMATED_LEVEL_SURFACE_SIZE}
        getItemType={levelsListRowItemType}
        initialScrollIndex={rowScrollIndex}
        keyExtractor={levelsListRowKeyExtractor}
        ListFooterComponent={ListFooterComponent}
        ListHeaderComponent={ListHeaderComponent}
        numColumns={surfaceColumns}
        onLayout={(event) => setLayoutWidth(event.nativeEvent.layout.width)}
        onViewableItemsChanged={onViewableItemsChanged}
        overrideItemLayout={(layout, item) => {
          layout.span = item.kind === "upgrade-prompt" ? surfaceColumns : 1;
        }}
        recycleItems
        ref={listRef}
        renderItem={renderItem}
        // Sticky needs an Animated ScrollView; LegendList drives the pinned row off its scroll value.
        renderScrollComponent={(props) => <AnimatedScrollView {...props} />}
        scrollEventThrottle={16}
        // Released whenever another route covers Browse; reopen at the row the learner left.
        scrollPosition={{ key: "levels-list", scope: scrollPositionScope }}
        // Only reserve chrome space when the list actually extends beneath floating glass.
        stickyHeaderConfig={{ offset: glassHeaderInset ?? contentTopPadding }}
        stickyHeaderIndices={promptIndex === null ? undefined : [promptIndex]}
        suppressContentGap
        testID="browse-levels-list"
        viewabilityConfig={viewabilityConfig}
      />
      {levelDetailsSheetGate !== null && (
        <LevelDetailsSheet
          gate={levelDetailsSheetGate}
          onNavigate={setActiveLevelPosition}
          ref={levelDetailsSheetRef}
        />
      )}
    </>
  );
}

/**
 * Anchors a glyph-tiles tile's slide pages at its bottom corners, just inside the tile's 1px
 * border: the slides before the card on the left, the slides after it on the right.
 */
const FRONT_BUTTON_SLIDE_PAGES_CLASS_NAME = "absolute inset-0 items-end justify-between p-px";

/** A preview card's glyph fills its top-left corner, so its slide pages sit in the top-right one. */
const PREVIEW_TILE_SLIDE_PAGES_CLASS_NAME = "absolute top-1.5 right-1.5 gap-0.5";

/** A glyph-tiles surface's grid rows of character glyphs. */
function useLevelSurfaceGlyphGridRows({
  characters,
  columns,
}: {
  characters: readonly string[];
  columns: number;
}) {
  const [getToggledGraphemeGlyph] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.getToggledGraphemeGlyph,
  );
  return useMemo(
    () =>
      split(
        characters.map((character) => getToggledGraphemeGlyph(character)),
        columns,
      ),
    [characters, columns, getToggledGraphemeGlyph],
  );
}

function LevelButtonsSurface({
  batchedTileData,
  columns,
  completedByGrapheme,
  defaultExpanded,
  getCardStage,
  glyphStyle,
  indexedLevel,
  onHeaderPress,
  onNavigate,
}: {
  batchedTileData: BatchedGraphemeTileData;
  columns: number;
  completedByGrapheme: ReadonlyMap<string, boolean> | undefined;
  defaultExpanded: boolean;
  getCardStage: GetGraphemeCardStage;
  glyphStyle?: GlyphStyle;
  indexedLevel: IndexedLevel;
  onHeaderPress: () => void;
  onNavigate: (morpheme: string) => void;
}) {
  const { level } = indexedLevel;
  const { expanded, setExpanded } = useLevelDetailsExpanded(
    level.index,
    "character",
    defaultExpanded,
  );
  const gridRows = useLevelSurfaceGlyphGridRows({ characters: level.characters, columns });
  const levelTileData = useLevelTileData({
    getCardStage,
    level,
    part: "character",
    tileData: batchedTileData,
  });
  const renderSlidePages = useLevelSurfaceSlidePages({
    className: FRONT_BUTTON_SLIDE_PAGES_CLASS_NAME,
    levelIndex: level.index,
    part: "character",
  });

  // Stage + pinyin-slice come from the WHOLE-list batched queries (see `LevelButtonsLevelsList`), so
  // this surface — and its tiles — fire no query of their own.
  //
  // A meaning-only level's cards render plain: the learner meets their pronunciations only in the
  // pronunciation level that follows (`2-P1`), so tone colour would teach a reading too early.
  return (
    <Surface className="mb-1" testID={`level-${level.index + 1}`}>
      <LevelDetailsSurfaceHeader
        expanded={expanded}
        label={getLevelsListEntryLabel(indexedLevel)}
        level={level}
        onExpandedChange={setExpanded}
        onPress={onHeaderPress}
      />
      {expanded ? (
        <LevelDetailTiles
          characters={level.characters}
          getStage={levelTileData.getStage}
          levelIndex={level.index}
          part="character"
        />
      ) : (
        gridRows.map((rowGlyphs, rowIndex) => (
          <LevelSurfaceGlyphGrid
            batchedTileData={levelTileData}
            columns={columns}
            completedByGrapheme={completedByGrapheme}
            firstSlotIndex={rowIndex * columns}
            glyphs={rowGlyphs}
            glyphStyle={glyphStyle}
            isLastInLevel={rowIndex === arrayLength(gridRows) - 1}
            key={rowIndex}
            onNavigate={onNavigate}
            renderTileOverlay={renderSlidePages}
            uncolored={level.meaningOnly === true}
          />
        ))
      )}
    </Surface>
  );
}

const NO_PRONUNCIATION_CHARACTERS: readonly string[] = [];

/**
 * The pronunciation level after a meaning-only run (`Level 1-P1`): the same characters again, now
 * tone-coloured, since this level introduces their readings. Words authored on a pronunciation
 * level (3-P3's 个子 / 这个) stay at the end of that same surface. It has no details sheet.
 */
function PronunciationLevelButtonsSurface({
  batchedTileData,
  columns,
  completedByGrapheme,
  defaultExpanded,
  getCardStage,
  glyphStyle,
  indexedPronunciationLevel,
  onNavigate,
  wordState,
}: {
  batchedTileData: BatchedGraphemeTileData;
  columns: number;
  completedByGrapheme: ReadonlyMap<string, boolean> | undefined;
  defaultExpanded: boolean;
  getCardStage: GetGraphemeCardStage;
  glyphStyle?: GlyphStyle;
  indexedPronunciationLevel: IndexedPronunciationLevel;
  onNavigate: (morpheme: string) => void;
  wordState: WordLevelsListState;
}) {
  const { isLocked, pronunciationLevel: level } = indexedPronunciationLevel;
  const { expanded, setExpanded } = useLevelDetailsExpanded(
    level.index,
    "pronunciation",
    defaultExpanded,
  );
  const characters = level.pronunciationCharacters ?? NO_PRONUNCIATION_CHARACTERS;
  const words = level.pronunciationWords ?? NO_PRONUNCIATION_CHARACTERS;
  const lessons = useMemo(() => [...characters, ...words], [characters, words]);
  const label = getLevelsListEntryLabel(indexedPronunciationLevel);
  const gridRows = useLevelSurfaceGlyphGridRows({ characters, columns });
  // Each tile is its reading's pronunciation card: a character whose meaning is learned stays
  // unshaded here until this level's lesson enrolls the reading.
  const levelTileData = useLevelTileData({
    getCardStage,
    level,
    part: "pronunciation",
    tileData: batchedTileData,
  });
  const renderSlidePages = useLevelSurfaceSlidePages({
    className: FRONT_BUTTON_SLIDE_PAGES_CLASS_NAME,
    levelIndex: level.index,
    part: "pronunciation",
  });

  if (isLocked === true) {
    return (
      <LockedLevelSurface characters={lessons} columns={columns} label={label} level={level} />
    );
  }
  return (
    <Surface
      className="mb-1"
      testID={`pronunciation-level-${formatPronunciationLevelLabel(level, indexedPronunciationLevel.pronunciationIndex ?? 0)}`}
    >
      <LevelDetailsSurfaceHeader
        characters={lessons}
        expanded={expanded}
        label={label}
        level={level}
        onExpandedChange={setExpanded}
        part="pronunciation"
      />
      {expanded ? (
        <LevelDetailTiles
          characters={characters}
          getStage={levelTileData.getStage}
          levelIndex={level.index}
          part="pronunciation"
        />
      ) : (
        <>
          {gridRows.map((rowGlyphs, rowIndex) => (
            <LevelSurfaceGlyphGrid
              batchedTileData={levelTileData}
              columns={columns}
              completedByGrapheme={completedByGrapheme}
              firstSlotIndex={rowIndex * columns}
              glyphs={rowGlyphs}
              glyphStyle={glyphStyle}
              isLastInLevel={words.length === 0 && rowIndex === arrayLength(gridRows) - 1}
              key={rowIndex}
              onNavigate={onNavigate}
              renderTileOverlay={renderSlidePages}
              showPrimaryPinyin
            />
          ))}
        </>
      )}
      {words.length > 0 ? (
        <WordLevelGlyphGrid
          columns={columns}
          onNavigate={wordState.onNavigate}
          tileSize={wordState.tileSize}
          words={words}
        />
      ) : null}
    </Surface>
  );
}

function WordLevelEntrySurface({
  indexedWordLevel: { isLocked, wordLevel },
  state,
}: {
  indexedWordLevel: IndexedWordLevel;
  state: WordLevelsListState;
}) {
  return isLocked === true ? (
    <LockedWordLevelSurface columns={state.columns} level={wordLevel} />
  ) : (
    <WordLevelSurface level={wordLevel} state={state} />
  );
}

const LEVEL_PREVIEW_TILE_COLUMN_COUNT = 4;

/** A preview-mode surface's cards, with its slide lessons as pages on the cards they play next to. */
function LevelDetailTiles({
  characters,
  getStage,
  levelIndex,
  part,
}: {
  characters: readonly string[];
  /** The level's tile stages (`useLevelTileData`), by front: a detail tile has no slot. */
  getStage: (front: string) => MorphemeGlyphTileStage;
  levelIndex: number;
  part: LevelSurfacePart;
}) {
  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  // A pronunciation level lists a character once per reading it teaches, but the details-tile
  // slice is per character: query each once (repeated keys log "Duplicate Queries found").
  const uniqueCharacters = useMemo(() => uniq([...characters]), [characters]);
  const results = useQueries({
    queries: uniqueCharacters.map((morpheme) =>
      queryOptions({
        queryFn: morphemeDetailsTileSliceQueryFn,
        queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme }),
      }),
    ),
  });
  const cards = useMemo<MorphemeDetailsTileSlice[]>(() => {
    const cardByCharacter = new Map(
      uniqueCharacters.map((character, index) => [character, results[index]?.data ?? null]),
    );
    return filterNullable(characters.map((character) => cardByCharacter.get(character) ?? null));
  }, [characters, results, uniqueCharacters]);
  const renderSlidePages = useLevelSurfaceSlidePages({
    className: PREVIEW_TILE_SLIDE_PAGES_CLASS_NAME,
    levelIndex,
    part,
  });
  // Character count is known from the level entity; reserve that many compact card tiles so expand
  // does not collapse to an empty body while the details-tile slices resolve.
  if (results.some((result) => result.isPending)) {
    const tileCount = arrayLength(characters);
    return (
      <CardDetailsTileListSkeleton
        columnCount={LEVEL_PREVIEW_TILE_COLUMN_COUNT}
        maxRowCount={Math.ceil(tileCount / LEVEL_PREVIEW_TILE_COLUMN_COUNT)}
        tileCount={tileCount}
      />
    );
  }
  return (
    <MorphemeDetailsTileList
      cards={cards}
      columnCount={LEVEL_PREVIEW_TILE_COLUMN_COUNT}
      compact
      getStage={getStage}
      renderCardOverlay={renderSlidePages}
      scrollable={false}
    />
  );
}
